import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { IconButton, Tooltip, Typography } from '@material-ui/core';
import { Delete, Visibility, CloudDownload, InfoOutlined } from '@material-ui/icons';
import { AiOutlineTable } from 'react-icons/ai';
import moment from 'moment';

import Loading from '../../../Components/Loading';
import SemDados from '../../../Components/SemDados';
import TableCustom from '../../../Components/Table';
import ModalExclusao from '../../../Components/ModalExclusao';
import LoadingButton from '../../../Components/LoadingButton';
import DrawerVisualizarResultado from './DrawerVisualizarResultado';

import { excluirResultado, exportarRelatorio, exportarRelatorioDetalhadoCompleto, listarResultados } from '../../../Store/Actions/API/resultadosActions';
import { setParamsFilterResultados } from '../../../Store/Actions/Reducer/seguradoActions';
import { formatarReais } from '../../../Utils/utils';
import { useStyles } from './styles';

export default function Resultados({ cpf }) {

    const classes = useStyles();
    const dispatch = useDispatch();
    const paramsFiltroResultados = useSelector((state) => state.segurado.paramsFiltroResultados, shallowEqual);

    const [isLoading, setLoading] = useState(false);
    const [isLoadingExportar, setLoadingExportar] = useState(false);
    const [isLoadingExportarCompleto, setLoadingExportarCompleto] = useState(false);
    const [isLoadingExcluir, setLoadingExcluir] = useState(false);

    const [resultados, setResultados] = useState([]);
    const [quantidadeBanco, setQuantidadeBanco] = useState(0);
    const [creditoTotal, setCreditoTotal] = useState(null);
    const [creditoTotalSelic, setCreditoTotalSelic] = useState(null);

    const [resultadoSelecionado, setResultadoSelecionado] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [openModalExclusao, setOpenModalExclusao] = useState(false);

    const [bloquearExclusao, setBloquearExclusao] = useState(false);

    useEffect(() => {
        lidarListagemResultados();
    }, [paramsFiltroResultados]); // eslint-disable-line

    const lidarListagemResultados = async () => {
        const filtros = {
            ...paramsFiltroResultados,
            cpf
        };

        setLoading(true);

        const response = await listarResultados(filtros);

        if (response !== null) {
            setQuantidadeBanco(response.quantidade ?? 0);
            setResultados(response.resultados ?? []);
            setCreditoTotal(response.creditoTotal ?? 0);
            setCreditoTotalSelic(response.creditoTotalSelic ?? 0);
            setBloquearExclusao(response.bloquearExclusao ?? false);
        }

        setLoading(false);
    }

    const lidarExportarRelatorio = async () => {
        setLoadingExportar(true);
        await exportarRelatorio(cpf);
        setLoadingExportar(false);
    }

    const lidarExportarRelatorioDetalhadoCompleto = async () => {
        setLoadingExportarCompleto(true);
        await exportarRelatorioDetalhadoCompleto({ cpf });
        setLoadingExportarCompleto(false);
    }

    const onChangePagination = (pag) => {
        dispatch(setParamsFilterResultados(pag));
    }

    const clicaExcluirResultado = (row) => {
        setResultadoSelecionado({ competencia: row.competencia });
        setOpenModalExclusao(true);
    }

    const cancelaExcluirResultado = () => {
        setResultadoSelecionado(null);
        setOpenModalExclusao(false);
    }

    const confirmaExcluirResultado = async () => {
        if (resultadoSelecionado) {
            setLoadingExcluir(true);
            await excluirResultado(cpf, resultadoSelecionado.competencia);
            setLoadingExcluir(false);
            setResultadoSelecionado(null);
            setOpenModalExclusao(false);
            lidarListagemResultados();
        }
    }

    const clicaVisualizarResultado = (row) => {
        setResultadoSelecionado({ competencia: row.competencia });
        setOpenDrawer(true);
    }

    const fechaVisualizarResultado = () => {
        setResultadoSelecionado(null);
        setOpenDrawer(false);
    }

    const columns = [
        {
            title: "Competência",
            dataIndex: "competencia",
            key: "competencia",
            width: 30,
            headerRender: (title) => <div style={{ paddingLeft: 15, display: 'flex' }}>
                <Typography style={{ fontSize: 12, fontWeight: 700 }}>{title}</Typography>
            </div>,
            render: (value, row) => <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <div style={{ width: 20, marginRight: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {row.possuiBeneficio && <Tooltip title={<Typography style={{ whiteSpace: 'pre-line', wordBreak: 'break-word', fontSize: 13 }}>
                        {`Houve o recebimento de benefícios no período. (${row.especieBeneficio ?? 'Espécie não informada'})`}
                    </Typography>} arrow placement='right'>
                        <InfoOutlined htmlColor='orange' style={{cursor: 'pointer'}} size={17} fontSize='small' />
                    </Tooltip>}
                </div>
                <Typography style={{ fontSize: 13 }}>{value}</Typography>
            </div>,
        },
        {
            title: "Valor Máximo Salário-Contribuição",
            dataIndex: "valorMaximoSalarioContribuicao",
            key: "valorMaximoSalarioContribuicao",
            align: "center",
            width: 155,
            render: (value, row) => <p style={{ fontSize: 13 }}>{(value !== '-') ? formatarReais(value) : '-'}</p>,
        },
        {
            title: "Recolhimento Máximo",
            dataIndex: "recolhimentoMaximo",
            key: "recolhimentoMaximo",
            align: "center",
            render: (value, row) => <p style={{ fontSize: 13 }}>{(value !== '-') ? formatarReais(value) : '-'}</p>,
        },
        {
            title: "Remuneração Recebida",
            dataIndex: "remuneracao",
            key: "remuneracao",
            align: "center",
            render: (value, row) => <p style={{ fontSize: 13 }}>{(value !== '-') ? formatarReais(value) : '-'}</p>,
        },
        {
            title: "INSS Retido",
            dataIndex: "retencaoINSS",
            key: "retencaoINSS",
            align: "center",
            render: (value, row) => <p style={{ fontSize: 13 }}>{(value !== '-') ? formatarReais(value) : '-'}</p>,
        },
        {
            title: "Valor do Crédito",
            dataIndex: "valorCredito",
            key: "valorCredito",
            align: "center",
            render: (value, row) => <p style={{ fontSize: 13 }}>{(value !== '-') ? formatarReais(value) : '-'}</p>,
        },
        {
            title: "Data Selic",
            dataIndex: "dataSelic",
            key: "dataSelic",
            align: "center",
            render: (value, row) => <p style={{ fontSize: 13 }}>{(value !== '-') ? moment(value).format('MM/YYYY') : '-'}</p>,
        },
        {
            title: "Percentual Selic",
            dataIndex: "percentualSelic",
            key: "percentualSelic",
            align: "center",
            render: (value, row) => <p style={{ fontSize: 13 }}>{(value !== '-') ? `${value}%`.replace('.', ',') : '-'}</p>,
        },
        {
            title: "Crédito Atualizado Selic",
            dataIndex: "creditoAtualizado",
            key: "creditoAtualizado",
            align: "center",
            render: (value, row) => <p style={{ fontSize: 13 }}>{(value !== '-') ? formatarReais(value) : '-'}</p>,
        },
        {
            title: "Ações",
            dataIndex: "acoes",
            key: "acoes",
            align: "center",
            render: (value, row) => <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ paddingRight: 3 }}>
                    <Tooltip title="Visualizar resultado" arrow>
                        <IconButton size='small' onClick={() => clicaVisualizarResultado(row)}>
                            <Visibility fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                </div>
                <div style={{ paddingLeft: 3 }}>
                    <Tooltip title="Excluir resultado" arrow>
                        <IconButton disabled={bloquearExclusao} size='small' onClick={() => clicaExcluirResultado(row)}>
                            <Delete />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>,
        }
    ];

    const propsTable = {
        columns,
        data: resultados,
        optionsPagination: {
            count: quantidadeBanco,
            pagination: { ...paramsFiltroResultados },
            onChangePagination,
        },
        optionsToolbar: {
            info: (
                <div className={classes.rowHeader}>
                    <div className={classes.colTitle}>
                        <div style={{ paddingLeft: 5 }}>
                            <Typography className={classes.headerTitle}>Apuração do crédito</Typography>
                        </div>
                        <div style={{ marginTop: 5, marginBottom: 2 }}>
                            <Typography className={classes.subheader}>
                                <span>
                                    <b>Total do crédito apurado:</b>{" "}
                                    {!Number.isNaN(creditoTotal) ? formatarReais(creditoTotal) : '-'}
                                </span>
                            </Typography>
                        </div>
                        <div style={{ marginBottom: 5 }}>
                            <Typography className={classes.subheader}>
                                <span>
                                    <b>Total do crédito atualizado pela SELIC:</b>{" "}
                                    {!Number.isNaN(creditoTotalSelic) ? formatarReais(creditoTotalSelic) : '-'}
                                </span>
                            </Typography>
                        </div>
                    </div>
                    <div className={classes.colButton}>
                        <div style={{ marginRight: 15 }}>
                            <LoadingButton
                                id="button_exportar_planilha"
                                variant="contained"
                                color="inherit"
                                startIcon={<AiOutlineTable color={isLoadingExportar ? 'gray' : '#000000'} />}
                                onClick={lidarExportarRelatorio}
                                size={'small'}
                                loading={isLoadingExportar}
                                style={{ color: isLoadingExportar ? 'gray' : '#000000' }}
                            >
                                Exportar planilha
                            </LoadingButton>
                        </div>
                        <div>
                            <LoadingButton
                                id="button_exportar_relatorio_completo"
                                variant="contained"
                                color="primary"
                                startIcon={<CloudDownload color={isLoadingExportarCompleto ? 'gray' : '#000000'} />}
                                onClick={lidarExportarRelatorioDetalhadoCompleto}
                                size={'small'}
                                loading={isLoadingExportarCompleto}
                                style={{ color: isLoadingExportarCompleto ? 'gray' : '#000000' }}
                            >
                                Exportar planilha detalhada
                            </LoadingButton>
                        </div>
                    </div>
                </div >
            ),
        },
        heightBody: "calc(100vh - 465px)",
        minHeightBody: "calc(100vh - 465px)"
    };

    return (
        <>
            {(openDrawer && resultadoSelecionado !== null) && <DrawerVisualizarResultado
                open={openDrawer}
                fechar={fechaVisualizarResultado}
                resultadoSelecionado={resultadoSelecionado}
                cpf={cpf}
            />}
            {(openModalExclusao && resultadoSelecionado !== null) && <ModalExclusao
                open={openModalExclusao}
                titulo={'Excluir Resultado'}
                onConfirma={confirmaExcluirResultado}
                loading={isLoadingExcluir}
                onCancela={cancelaExcluirResultado}
                textoComponent={<span>
                    Tem certeza que deseja apagar o resultado da competência
                    <b>{" " + resultadoSelecionado.competencia}</b>?{" "}
                    Todos os registros serão apagados e não será possível resgatá-los
                    novamente.
                </span>}
            />}
            {(!isLoading && resultados.length === 0) && (
                <SemDados text="Nenhum registro encontrado" />
            )}
            {(!isLoading && resultados.length > 0) && (
                <TableCustom {...propsTable} />
            )}
            {isLoading ? <Loading text={"Carregando resultados..."} /> : null}
        </>
    )
}